/*
 * Legal links (docs/api.md, "Legal links"): Hosterr's Terms of service, Privacy policy, Acceptable use policy and Data
 * processing agreement, as its admins set them, and an agency's own terms on its brand. The sign-in pages show the
 * ones that are set; an older API without them shows none.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

export type LegalSettings = {
  termsUrl: string | null;
  privacyUrl: string | null;
  acceptableUseUrl: string | null;
  dpaUrl: string | null;
  /** A label for the terms ("2026-10"), recorded with each new account and accepted invite. */
  termsVersion: string | null;
};

/** What a page shows: Hosterr's links, and the agency's own terms when the page is in its brand. */
export type Legal = LegalSettings & { agency: { orgId: string; name: string; termsUrl: string } | null };

/** The links of the brand this address shows, for the sign-in pages. Null until known, or from an older API. */
export function useLegal() {
  const q = useQuery({
    queryKey: ["legal", "host"],
    queryFn: ({ signal }) => api.get<{ legal: Legal }>("/legal", signal),
    select: (d) => d.legal,
    staleTime: 5 * 60_000,
    retry: false,
  });
  return q.data ?? null;
}

const settingsKey = ["platform", "legal"] as const;

/** Hosterr's own links, for its owners and admins. */
export function useLegalSettings(enabled = true) {
  return useQuery({
    queryKey: settingsKey,
    queryFn: ({ signal }) => api.get<{ legal: LegalSettings; updatedAt: string | null }>("/platform/legal", signal),
    enabled,
  });
}

export function useUpdateLegal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: Partial<LegalSettings>) => api.patch<{ legal: LegalSettings; updatedAt: string | null }>("/platform/legal", v),
    onSuccess: (d) => {
      qc.setQueryData(settingsKey, d);
      void qc.invalidateQueries({ queryKey: ["legal"] });
    },
  });
}
